/**
 * TaskDetailModals Component
 * 
 * Combined file containing all modal components for Task Detail page
 * Note: EditTaskModal removed - using AddTaskModal from listing page instead
 */

import { useState } from 'react';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';

/**
 * AddSubtaskModal - Modal for adding new subtask
 */
export const AddSubtaskModal = ({ show, onHide, taskId }) => {
    const [subtaskName, setSubtaskName] = useState('');
    const [selectedAssignee, setSelectedAssignee] = useState(null);
    const [dueDate, setDueDate] = useState(new Date());
    const [description, setDescription] = useState('');

    const assigneeOptions = [
        { value: '1', label: 'Sarah Wilson' },
        { value: '2', label: 'Mike Johnson' },
        { value: '3', label: 'John Doe' }
    ];

    const handleSubmit = () => {
        console.log('Adding subtask:', { taskId, subtaskName, selectedAssignee, dueDate, description });
        setSubtaskName('');
        setSelectedAssignee(null);
        setDueDate(new Date());
        setDescription('');
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Add Subtask</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Subtask Name <span className='text-danger'>*</span></Form.Label>
                                <Form.Control 
                                    type="text"
                                    value={subtaskName}
                                    onChange={(e) => setSubtaskName(e.target.value)}
                                    placeholder="Enter subtask name"
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Assign To <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    value={selectedAssignee}
                                    onChange={setSelectedAssignee}
                                    options={assigneeOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select assignee"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Due Date <span className='text-danger'>*</span></Form.Label>
                                <Flatpickr
                                    value={dueDate}
                                    onChange={([date]) => setDueDate(date)}
                                    className="form-control"
                                    options={{
                                        dateFormat: 'd M Y',
                                        allowInput: true,
                                        disableMobile: true
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Enter subtask description"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <div>
                    <Button variant="primary" className='me-3' onClick={handleSubmit}>
                        Add & Another
                    </Button>
                    <Button variant="primary" onClick={() => {
                        handleSubmit();
                        onHide();
                    }}>
                        Add Subtask
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * UploadAttachmentModal - Modal for uploading files
 */
export const UploadAttachmentModal = ({ show, onHide, taskId }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileDescription, setFileDescription] = useState('');

    const handleFileSelect = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleSubmit = () => {
        console.log('Uploading file:', { taskId, selectedFile, fileDescription });
        setSelectedFile(null);
        setFileDescription('');
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Upload Attachment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Select File <span className='text-danger'>*</span></Form.Label>
                                <Form.Control 
                                    type="file"
                                    onChange={handleFileSelect}
                                />
                                {selectedFile && (
                                    <div className="mt-2 p-2 bg-light rounded">
                                        <i className="bi bi-file-earmark me-2"></i>
                                        {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Description</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3}
                                    value={fileDescription}
                                    onChange={(e) => setFileDescription(e.target.value)}
                                    placeholder="Add a description for this file (optional)"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!selectedFile}>
                    Upload File
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * AddCommentModal - Modal for adding comments
 */
export const AddCommentModal = ({ show, onHide, taskId }) => {
    const [comment, setComment] = useState('');

    const handleSubmit = () => {
        console.log('Adding comment:', { taskId, comment });
        setComment('');
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Add Comment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group>
                        <Form.Label>Comment <span className='text-danger'>*</span></Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={5}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Write your comment here..."
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!comment.trim()}>
                    Add Comment
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * ChangeStatusModal - Modal for changing task status
 */
export const ChangeStatusModal = ({ show, onHide, currentStatus, taskId }) => {
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [note, setNote] = useState('');

    const statusOptions = [
        { value: 'backlog', label: 'Backlog', color: '#6b7280' },
        { value: 'planning', label: 'Planning', color: '#3b82f6' },
        { value: 'in-progress', label: 'In Progress', color: '#f59e0b' },
        { value: 'completed', label: 'Completed', color: '#10b981' },
        { value: 'on-hold', label: 'On Hold', color: '#8b5cf6' },
        { value: 'cancelled', label: 'Cancelled', color: '#ef4444' }
    ];

    const handleSubmit = () => {
        console.log('Changing status:', { taskId, selectedStatus, note });
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Change Task Status</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Current Status</Form.Label>
                                <div className="p-2 bg-light rounded">
                                    <Badge bg="warning" className="text-uppercase">{currentStatus}</Badge>
                                </div>
                            </Form.Group>
                        </Col>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>New Status <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    options={statusOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select new status"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Note (Optional)</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Add a note about this status change..."
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!selectedStatus}>
                    Change Status
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * UpdatePriorityModal - Modal for updating task priority
 */
export const UpdatePriorityModal = ({ show, onHide, currentPriority, taskId }) => {
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [note, setNote] = useState('');

    const priorityOptions = [
        { value: 'high', label: 'High', color: 'danger' },
        { value: 'medium', label: 'Medium', color: 'warning' },
        { value: 'low', label: 'Low', color: 'success' }
    ];

    const handleSubmit = () => {
        console.log('Updating priority:', { taskId, selectedPriority, note });
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
            <Modal.Header closeButton>
                <Modal.Title className='display-1'>Update Task Priority</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row className='gy-3'>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Current Priority</Form.Label>
                                <div className="p-2 bg-light rounded">
                                    <Badge bg="danger" className="text-uppercase">{currentPriority}</Badge>
                                </div>
                            </Form.Group>
                        </Col>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>New Priority <span className='text-danger'>*</span></Form.Label>
                                <Select
                                    value={selectedPriority}
                                    onChange={setSelectedPriority}
                                    options={priorityOptions}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select new priority"
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={12}>
                            <Form.Group>
                                <Form.Label>Note (Optional)</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Add a note about this priority change..."
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={!selectedPriority}>
                    Update Priority
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/**
 * ViewAssigneesModal - Show all assigned users with details
 */
export const ViewAssigneesModal = ({ show, onHide, assignees }) => {
    if (!assignees || assignees.length === 0) return null;

    return (
        <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="display-1">Assigned Team Members</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="c-card-1">
                    <div className="c-card-body-1 p-0">
                        <div className="table-responsive">
                            <table className="c-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Member</th>
                                        <th className="text-center">Department</th>
                                        <th className="text-center">Designation</th>
                                        <th className="text-center">Contact</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignees.map((assignee) => (
                                        <tr key={assignee.id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {assignee.avatar ? (
                                                        <img 
                                                            src={assignee.avatar} 
                                                            alt={assignee.name} 
                                                            className="rounded-circle" 
                                                            style={{ width: '36px', height: '36px' }} 
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="c-avatar-text c-avatar-sm"
                                                        >
                                                            {assignee.initials}
                                                        </div>
                                                    )}
                                                    <span className="fw-semibold">{assignee.name}</span>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="p-tag">{assignee.department || 'N/A'}</div>
                                            </td>
                                            <td className="text-center">
                                                <div className="p-tag">{assignee.designation || 'N/A'}</div>
                                            </td>
                                            <td className="text-center">
                                                {assignee.email ? (
                                                    <a href={`mailto:${assignee.email}`} className="text-decoration-none text-primary">
                                                        <i className="fas fa-envelope me-1"></i>
                                                        {assignee.email}
                                                    </a>
                                                ) : (
                                                    <div className="p-tag">N/A</div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className='d-flex justify-content-start'>
                <Button variant="link" className='text-danger text-decoration-none' onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
